import { useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"

import {
  resetBorrowFlow,
  selectRepayStrategy,
  useDappSelector,
} from "#/redux-state"
import { useRepayBorrowTransaction } from "#/shared/hooks"
import { BorrowRepayStrategy, MezoModalProps } from "#/shared/types"
import { ComponentStepProps, StepperModal } from "#/ui/Modal"

import RepayAction from "./RepayAction"
import RepayReview from "./RepayReview"
import RepayStrategy from "./RepayStrategy"
import RepayTransaction from "./RepayTransaction"

export default function RepayBorrowModal({
  onClose,
  ...props
}: MezoModalProps) {
  const dispatch = useDispatch()

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const repayStrategy = useDappSelector(selectRepayStrategy)

  const handleClose = useCallback(() => {
    dispatch(resetBorrowFlow())
    onClose()
  }, [dispatch, onClose])

  const { repayHandler, hasError, isLoading } = useRepayBorrowTransaction()

  const steps = useMemo(() => {
    const actionStep = {
      title: "Repay loan",
      component: (stepProps: ComponentStepProps) => (
        <RepayAction {...stepProps} />
      ),
      withCloseButton: true,
    }

    const partialRepayStrategyStep = {
      title: "Select strategy",
      component: (stepProps: ComponentStepProps) => (
        <RepayStrategy {...stepProps} />
      ),
      withCloseButton: true,
    }

    const reviewStep = {
      title: "Review repayment",
      withCloseButton: true,
      component: (stepProps: ComponentStepProps) => (
        <RepayReview
          {...stepProps}
          handleRepay={repayHandler}
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
        />
      ),
    }

    const transactionStep = {
      title:
        !hasError && !isLoading ? "Repayment successful" : "Sign transaction",
      withCloseButton: !hasError && !isLoading,
      withBackButton: hasError,
      component: (stepProps: ComponentStepProps) => (
        <RepayTransaction
          {...stepProps}
          hasError={hasError}
          isLoading={isLoading}
        />
      ),
    }

    return repayStrategy === BorrowRepayStrategy.PARTIAL
      ? [actionStep, partialRepayStrategyStep, reviewStep, transactionStep]
      : [actionStep, reviewStep, transactionStep]
  }, [hasAcceptedTerms, hasError, isLoading, repayHandler, repayStrategy])

  return (
    <StepperModal
      {...props}
      variant="tall"
      onClose={handleClose}
      steps={steps}
    />
  )
}
